import BasePlayer from './BasePlayer';
import { getNextPlayRequestId } from '../common/channelPlayerState';
import { tLog, tWarn, tError } from 'utils/device/tizenLog';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import {
  hasLegacyTizenEpAvPlayAPI,
  normalizeTizenStreamUrl,
} from 'utils/device/tizenPlatform';
import { createHttpStreamCompletedHandler, createHttpStreamErrorHandler, markChannelStreamReady } from '../common/channelStreamUrl';
import {
  applyAvplayDisplayMode,
  getVideoDimensionsFromAvplay,
  parseTizenVideoResolution,
} from '../common/channelVideoAspect';
import { applyAvplayBuffering } from '../common/avplayBuffering';
/**
 * Samsung Tizen TEP player (TZ > 6.0 — BU8000 and newer).
 *
 * Uses window.webapis.avplay loaded from:
 *   $WEBAPIS/avplayextension/avplayextension.js  (already in index.html)
 *
 * AU8000 (TZ <= 6.0) uses window.webapis.epavplay via b2bavplay.js — that is
 * TizenPlayer.js.  Do NOT merge these two classes; the namespace differs.
 */
class TizenTEPPlayer extends BasePlayer {
  constructor() {
    super();
    this.maxRetries = 3;
    /** @type {'avplay'|'epavplay'} */
    this._activeApi = 'avplay';
    /** True once tep-preview-active is on (native video visible through HTML hole). */
    this._tepHoleVisible = false;
    /** True after first successful preview reveal (enables fast channel-zap path). */
    this._tepLayoutReady = false;
    this._lastPreviewCoords = null;
    this._lastVideoDimensions = null;
    this._currentPlayUrl = null;
    this._onPreviewRevealResolve = null;
    this._initializePlayerObject();
  }

  isTepLayoutReady() {
    return this._tepLayoutReady;
  }

  _isTepChannelSwitch(isFullScreen) {
    return !isFullScreen && this._isTepDevice() && this._tepLayoutReady;
  }

  _getAvPlayApi(api) {
    if (!window.webapis) {
      return null;
    }
    return api === 'epavplay' ? window.webapis.epavplay : window.webapis.avplay;
  }

  _isTepDevice() {
    const list = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST);
    return !!(list && list.isTIZEN_TEP);
  }

  /** CSS class on html/body — survives styled-components re-renders (inline bg does not). */
  _enableTepPreviewHole() {
    if (!this._isTepDevice()) return;
    document.documentElement.classList.add('tep-preview-active');
    document.body.classList.add('tep-preview-active');
    tLog('[TEP]', 'tep-preview-active ON');
  }

  _enableTepPreviewPending() {
    if (!this._isTepDevice()) return;
    document.documentElement.classList.add('tep-preview-pending');
    document.body.classList.add('tep-preview-pending');
  }

  _disableTepPreviewPending() {
    document.documentElement.classList.remove('tep-preview-pending');
    document.body.classList.remove('tep-preview-pending');
  }

  _disableTepPreviewHole() {
    if (!this._isTepDevice()) return;
    this._tepHoleVisible = false;
    this._disableTepPreviewPending();
    document.documentElement.classList.remove('tep-preview-active');
    document.body.classList.remove('tep-preview-active');
    tLog('[TEP]', 'tep-preview-active OFF');
  }

  _applyPreviewRect(coords) {
    const playerState = this.getPlayerState();
    if (!playerState.gAVplayObj || !coords) return;
    try {
      playerState.gAVplayObj.setDisplayRect(coords.x, coords.y, coords.width, coords.height);
      this._applyHttpAspectMode(playerState.playRequestId);
    } catch (e) {
      tWarn('[TEP]', 'setDisplayRect failed: ' + (e.message || e));
    }
  }

  _applyHttpAspectMode(myRequestId) {
    if (myRequestId !== this.getPlayerState().playRequestId) {
      return;
    }

    const playerState = this.getPlayerState();
    const avplay = playerState.gAVplayObj;
    if (!avplay) {
      return;
    }

    var dimensions = this._lastVideoDimensions;
    if (!dimensions) {
      dimensions = getVideoDimensionsFromAvplay(avplay);
      if (dimensions) {
        this._lastVideoDimensions = dimensions;
      }
    }

    applyAvplayDisplayMode(avplay, dimensions);
  }

  _finishPreviewReveal() {
    if (this._onPreviewRevealResolve) {
      var resolve = this._onPreviewRevealResolve;
      this._onPreviewRevealResolve = null;
      resolve();
    }
  }

  _waitForPreviewReveal(timeoutMs) {
    if (this._tepHoleVisible) {
      return Promise.resolve();
    }
    var self = this;
    return new Promise(function (resolve) {
      var timer = setTimeout(function () {
        self._onPreviewRevealResolve = null;
        tWarn('[TEP]', 'preview reveal timeout — forcing reveal');
        self._revealTepPreviewVideo();
        resolve();
      }, timeoutMs);
      self._onPreviewRevealResolve = function () {
        clearTimeout(timer);
        resolve();
      };
    });
  }

  /**
   * Punch the transparent HTML hole only after the first video frame is ready.
   */
  _revealTepPreviewVideo() {
    if (!this._isTepDevice()) {
      return;
    }
    var coords = this._calculatePreviewCoords();
    this._lastPreviewCoords = coords;

    if (this._tepHoleVisible) {
      this._disableTepPreviewPending();
      this._applyPreviewRect(coords);
      tLog('[TEP]', 'reveal zap ' + JSON.stringify(coords));
      this._finishPreviewReveal();
      return;
    }
    this._tepHoleVisible = true;
    this._tepLayoutReady = true;
    this._disableTepPreviewPending();
    this._enableTepPreviewHole();
    this._positionAvPlayerElement(coords, false);
    this._applyPreviewRect(coords);
    tLog('[TEP]', 'reveal preview ' + JSON.stringify(coords));
    this._finishPreviewReveal();
  }

  async _resolvePreviewCoords(requireStable) {
    if (!requireStable && this._lastPreviewCoords) {
      return this._lastPreviewCoords;
    }
    if (!requireStable) {
      var measured = this._measurePreviewCoords();
      if (this._isPlausiblePreviewCoords(measured)) {
        this._lastPreviewCoords = measured;
        return measured;
      }
      if (this._lastPreviewCoords) {
        return this._lastPreviewCoords;
      }
    }
    var stable = await this._waitForStablePreviewCoords();
    this._lastPreviewCoords = stable;
    return stable;
  }

  /**
   * Preview rect from VideoPlayerArea (not #av-player — object may be position:fixed).
   */
  _measurePreviewCoords() {
    const anchor =
      document.querySelector('[data-tizen-video-area]') || document.getElementById('av-player');
    if (!anchor) {
      return null;
    }
    // eslint-disable-next-line no-unused-expressions
    anchor.offsetHeight;
    const rect = anchor.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    return {
      x: Math.round(rect.left),
      y: Math.round(rect.top),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    };
  }

  /** True when layout looks like 3-column TV (video column on the right, not full-width). */
  _isPlausiblePreviewCoords(coords) {
    return (
      coords &&
      coords.x >= 300 &&
      coords.width >= 400 &&
      coords.width <= 1500 &&
      coords.height >= 200
    );
  }

  _calculatePreviewCoords() {
    const coords = this._measurePreviewCoords();
    if (coords) {
      tLog('[TEP]', 'preview coords=' + JSON.stringify(coords));
      return coords;
    }
    tWarn('[TEP]', 'preview coords fallback');
    return { x: 500, y: 300, width: 854, height: 480 };
  }

  /**
   * Wait until channel UI is painted (navigating from landing fires play before VideoPlayerArea exists).
   * Without this, avplay briefly uses default fullscreen rect → black flash.
   */
  async _waitForStablePreviewCoords() {
    var prev = null;
    for (let i = 0; i < 24; i++) {
      const coords = this._measurePreviewCoords();
      if (this._isPlausiblePreviewCoords(coords)) {
        if (
          prev &&
          prev.x === coords.x &&
          prev.y === coords.y &&
          prev.width === coords.width &&
          prev.height === coords.height
        ) {
          tLog('[TEP]', 'preview layout stable ' + JSON.stringify(coords));
          return coords;
        }
        prev = coords;
      } else {
        prev = null;
      }
      await this.delay(50);
    }
    return this._calculatePreviewCoords();
  }

  _positionAvPlayerElement(coords, isFullScreen) {
    const player = document.getElementById('av-player');
    if (!player) return;

    if (isFullScreen) {
      player.classList.add('fullscreenMode');
      player.style.left = '';
      player.style.top = '';
      player.style.width = '';
      player.style.height = '';
      return;
    }

    player.classList.remove('fullscreenMode');
    player.style.left = coords.x + 'px';
    player.style.top = coords.y + 'px';
    player.style.width = coords.width + 'px';
    player.style.height = coords.height + 'px';
  }

  _clearAvPlayerElementPosition() {
    const player = document.getElementById('av-player');
    if (!player) return;
    player.classList.remove('fullscreenMode');
    player.style.left = '';
    player.style.top = '';
    player.style.width = '';
    player.style.height = '';
  }

  /** Apply setDisplayRect for preview or fullscreen; re-apply after layout/hole punch. */
  _syncPreviewDisplay(isFullScreen, channelSwitch) {
    const playerState = this.getPlayerState();
    if (!playerState.gAVplayObj) return;

    if (isFullScreen) {
      this._disableTepPreviewHole();
      const coords = { x: 0, y: 0, width: 1920, height: 1080 };
      this._positionAvPlayerElement(coords, true);
      playerState.gAVplayObj.setDisplayRect(coords.x, coords.y, coords.width, coords.height);
      this._applyHttpAspectMode(playerState.playRequestId);
      tLog('[TEP]', 'rect fullscreen ' + JSON.stringify(coords));
      return;
    }

    if (channelSwitch && this._lastPreviewCoords) {
      this._applyPreviewRect(this._lastPreviewCoords);
      tLog('[TEP]', 'rect zap ' + JSON.stringify(this._lastPreviewCoords));
      return;
    }

    this._resolvePreviewCoords(false).then((coords) => {
      this._applyPreviewRect(coords);
      tLog('[TEP]', 'rect preview ' + JSON.stringify(coords));
    });
  }

  _initializePlayerObject() {
    const playerState = this.getPlayerState();
    const gAVplayObj = this._getAvPlayApi(this._activeApi);
    if (!gAVplayObj || typeof gAVplayObj.open !== 'function') {
      if (this._activeApi === 'avplay' && hasLegacyTizenEpAvPlayAPI()) {
        tWarn('[TEP]', 'avplay missing — init epavplay');
        this._activeApi = 'epavplay';
        this._initializePlayerObject();
        return;
      }
      tError('[TEP]', 'player API NOT available: ' + this._activeApi);
      tError(
        '[TEP]',
        'webapis=' +
          typeof window.webapis +
          ' avplay=' +
          typeof (window.webapis && window.webapis.avplay) +
          ' epavplay=' +
          typeof (window.webapis && window.webapis.epavplay)
      );
    } else {
      tLog('[TEP]', '✅ webapis.' + this._activeApi + ' ready');
    }
    this.updatePlayerState({ gAVplayObj: gAVplayObj || null });
  }

  _switchToEpAvPlayFallback() {
    if (this._activeApi === 'epavplay') {
      return false;
    }
    if (!hasLegacyTizenEpAvPlayAPI()) {
      tError('[TEP]', 'no epavplay for SecurityError fallback');
      return false;
    }
    tWarn('[TEP]', 'avplay SecurityError — fallback to epavplay (B2B path)');
    this._activeApi = 'epavplay';
    this._initializePlayerObject();
    return true;
  }

  supportsFullscreenToggle() {
    return true;
  }

  async play(url, isFullScreen = false) {
    this._initializePlayerObject();
    tLog('[TEP]', 'play() url=' + (url || 'EMPTY') + ' fs=' + isFullScreen);

    if (!url) {
      tWarn('[TEP]', 'play() called with empty URL — skipping (channel not selected yet)');
      return;
    }

    var channelSwitch = this._isTepChannelSwitch(isFullScreen);

    if (!isFullScreen && this._isTepDevice()) {
      this._onPreviewRevealResolve = null;
      if (channelSwitch) {
        this._enableTepPreviewPending();
      } else {
        this._tepHoleVisible = false;
        this._disableTepPreviewHole();
        this._enableTepPreviewPending();
      }
    }

    // Prefer TEP avplay; reset each channel change so we retry avplay after a prior fallback
    this._activeApi = 'avplay';
    this._initializePlayerObject();

    if (!isFullScreen && this._isTepDevice() && !channelSwitch) {
      var measured = this._measurePreviewCoords();
      if (!this._isPlausiblePreviewCoords(measured)) {
        await this._waitForStablePreviewCoords();
      }
    }

    var streamUrl = normalizeTizenStreamUrl(url);
    tLog('[TEP]', 'stream url=' + streamUrl + (streamUrl !== url ? ' (was ' + url + ')' : ''));

    if (this._currentPlayUrl !== streamUrl) {
      this._lastVideoDimensions = null;
    }
    this._currentPlayUrl = streamUrl;

    this.updatePlayerState({ url: streamUrl });

    const myRequestId = getNextPlayRequestId();

    let playerState = this.getPlayerState();
    const currentState = playerState.gAVplayObj?.getState?.() || '';
    if (currentState === 'NONE' || currentState === 'READY' || currentState === 'IDLE') {
      this.updatePlayerState({ isPlayerBusy: false });
    }

    this._resetPlayerState();

    playerState = this.getPlayerState();
    this.updatePlayerState({ isPlayerBusy: true });

    try {
      await this._playWithRetry(myRequestId, isFullScreen, 0, channelSwitch);
      if (!isFullScreen && this._isTepDevice() && !channelSwitch) {
        await this._waitForPreviewReveal(8000);
      }
    } finally {
      const finalState = this.getPlayerState();
      if (myRequestId === finalState.playRequestId) {
        this.updatePlayerState({ isPlayerBusy: false });
      }
    }
  }

  async _playWithRetry(myRequestId, isFullScreen, retryCount = 0, channelSwitch = false) {
    const playerState = this.getPlayerState();
    const self = this;

    const listener = {
      onbufferingstart: () => console.log('[TEP Player] Buffering start'),
      onbufferingprogress: (percent) => console.log('[TEP Player] Buffering progress:', percent),
      onbufferingcomplete: () => {
        console.log('[TEP Player] Buffering complete');
        if (myRequestId === self.getPlayerState().playRequestId) {
          markChannelStreamReady(myRequestId);
          self._applyHttpAspectMode(myRequestId);
          if (!isFullScreen) {
            self._revealTepPreviewVideo();
          }
        }
      },
      oncurrentplaytime: (currentTime) => {
        console.log('[TEP Player] Current playtime:', currentTime);
        if (currentTime > 0 && myRequestId === self.getPlayerState().playRequestId) {
          self.updatePlayerState({ httpPlayTimeMs: currentTime, httpLastPlayAt: Date.now() });
          markChannelStreamReady(myRequestId);
        }
      },
      onevent: (eventType, eventData) => {
        console.log(`[TEP Player] Event: ${eventType}, Data: ${eventData}`);
        if (myRequestId !== self.getPlayerState().playRequestId) {
          return;
        }

        var resolution = parseTizenVideoResolution(eventType, eventData);
        if (resolution) {
          self._lastVideoDimensions = resolution;
          self._applyHttpAspectMode(myRequestId);
        }

        if (eventType === 'PLAYER_MSG_RENDER_DONE') {
          markChannelStreamReady(myRequestId);
          if (!isFullScreen) {
            self._revealTepPreviewVideo();
          }
        }
      },
      onerror: createHttpStreamErrorHandler(self, myRequestId),
      onsubtitlechange: () => console.log('[TEP Player] Subtitle changed'),
      ondrmevent: (drmEvent, drmData) =>
        console.log('[TEP Player] DRM Event:', drmEvent, 'Data:', drmData),
      onstreamcompleted: createHttpStreamCompletedHandler(self, myRequestId),
    };

    try {
      const preCheckState = this.getPlayerState();
      if (myRequestId !== preCheckState.playRequestId) {
        console.warn('[TEP Player] Aborting before start — newer request already in progress');
        return;
      }

      const initialState = playerState.gAVplayObj.getState();
      tLog('[TEP]', 'initialState=' + initialState);

      if (initialState !== 'NONE') {
        try {
          tWarn('[TEP]', 'not NONE — stop+close first');
          playerState.gAVplayObj.stop();
          playerState.gAVplayObj.close();
          this._disableTepPreviewHole();
          await this.delay(channelSwitch ? 20 : 100);
        } catch (cleanupErr) {
          tWarn('[TEP]', 'cleanup warn: ' + cleanupErr.name);
        }
      }

      if (!playerState.url) {
        tError('[TEP]', 'URL is undefined at open() — aborting');
        return;
      }
      tLog('[TEP]', 'calling open()...');
      playerState.gAVplayObj.open(playerState.url);
      tLog('[TEP]', '✅ open() returned');

      const immediateCheck = this.getPlayerState();
      if (myRequestId !== immediateCheck.playRequestId) {
        console.warn('[TEP Player] Aborting — newer request in progress');
        return;
      }

      if (!channelSwitch) {
        await this.delay(50);
      }

      const updatedState = this.getPlayerState();
      if (myRequestId !== updatedState.playRequestId) {
        console.warn('[TEP Player] Aborting — newer request in progress');
        return;
      }

      const stateAfterOpen = playerState.gAVplayObj.getState();
      tLog('[TEP]', 'stateAfterOpen=' + stateAfterOpen);

      if (stateAfterOpen !== 'IDLE') {
        tError('[TEP]', 'Expected IDLE after open, got: ' + stateAfterOpen);
        throw new Error(
          `[TEP Player] Player did not reach IDLE after open(). Got: ${stateAfterOpen}`
        );
      }

      // Absorb short UDP jitter on Hotel TV (must be IDLE, before prepare).
      applyAvplayBuffering(playerState.gAVplayObj, '[TEP Player]');

      const coords = isFullScreen
        ? { x: 0, y: 0, width: 1920, height: 1080 }
        : await this._resolvePreviewCoords(!channelSwitch);

      if (isFullScreen) {
        this._positionAvPlayerElement(coords, true);
        this._disableTepPreviewHole();
      } else if (channelSwitch) {
        this._enableTepPreviewPending();
      } else {
        this._tepHoleVisible = false;
        this._disableTepPreviewHole();
        this._enableTepPreviewPending();
      }
      this._applyPreviewRect(coords);

      playerState.gAVplayObj.setListener(listener);

      tLog('[TEP]', 'rect=' + JSON.stringify(coords) + ' fs=' + isFullScreen + ' zap=' + channelSwitch);
      tLog('[TEP]', 'calling prepareAsync()...');

      await this._prepareAsyncWithTimeout(myRequestId, retryCount, isFullScreen, channelSwitch);
    } catch (err) {
      tError('[TEP]', 'EXCEPTION name=' + err.name + ' msg=' + (err.message || '').slice(0, 80));

      if (err.name === 'SecurityError' && this._activeApi === 'avplay' && this._switchToEpAvPlayFallback()) {
        await this.delay(100);
        return this._playWithRetry(myRequestId, isFullScreen, retryCount, channelSwitch);
      }

      if (err.name === 'SecurityError') {
        tError(
          '[TEP]',
          'multicast blocked on api=' +
            this._activeApi +
            ' — Samsung must grant streamingtvplayer for F3YLc4YUuA.DotU'
        );
        throw err;
      }

      if (
        (err.name === 'InvalidStateError' ||
          err.name === 'InvalidAccessError' ||
          err.name === 'TypeError' ||
          err.message?.includes('Invalid state') ||
          err.message?.includes('Invalid operation') ||
          err.message?.includes('prepareAsync timeout') ||
          err.message?.includes('indexOf')) &&
        retryCount < this.maxRetries
      ) {
        tWarn('[TEP]', 'retrying ' + (retryCount + 1) + '/' + this.maxRetries);
        await this.delay(100);
        return this._playWithRetry(myRequestId, isFullScreen, retryCount + 1, channelSwitch);
      } else {
        tError('[TEP]', 'unrecoverable / max retries (api=' + this._activeApi + ')');
        throw err;
      }
    }
  }

  _prepareAsyncWithTimeout(myRequestId, retryCount, isFullScreen, channelSwitch) {
    const playerState = this.getPlayerState();
    const self = this;

    return new Promise((resolve, reject) => {
      var settled = false;
      function settle(fn, arg) {
        if (settled) return;
        settled = true;
        clearTimeout(prepareTimeout);
        fn(arg);
      }

      const prepareTimeout = setTimeout(() => {
        tWarn('[TEP]', 'prepareAsync timeout — force settle');
        try {
          playerState.gAVplayObj.stop();
          playerState.gAVplayObj.close();
        } catch (e) {}

        // Always settle — never leave play() hanging (app-wide freeze)
        if (myRequestId !== self.getPlayerState().playRequestId) {
          settle(resolve);
          return;
        }
        settle(reject, new Error('prepareAsync timeout'));
      }, 5000);

      playerState.gAVplayObj.prepareAsync(
        () => {
          const checkState = self.getPlayerState();
          if (myRequestId !== checkState.playRequestId) {
            console.warn('[TEP Player] Aborting prepareAsync callback — newer request in progress');
            settle(resolve);
            return;
          }

          const readyState = playerState.gAVplayObj.getState();
          tLog('[TEP]', '✅ prepareAsync done. state=' + readyState);

          if (readyState === 'READY' || readyState === 'IDLE') {
            try {
              playerState.gAVplayObj.play();
              tLog('[TEP]', '✅ play() called — PLAYING');
              self._syncPreviewDisplay(isFullScreen, channelSwitch);

              if (!isFullScreen) {
                var fallbackMs = channelSwitch ? 50 : 400;
                setTimeout(function () {
                  if (myRequestId !== self.getPlayerState().playRequestId) {
                    return;
                  }
                  if (channelSwitch && self._tepHoleVisible) {
                    self._revealTepPreviewVideo();
                    return;
                  }
                  if (!self._tepHoleVisible) {
                    tLog('[TEP]', 'fallback reveal after play');
                    self._revealTepPreviewVideo();
                  }
                }, fallbackMs);
              }

              setTimeout(() => {
                const timeCheckState = self.getPlayerState();
                if (myRequestId === timeCheckState.playRequestId) {
                  const currentState = playerState.gAVplayObj.getState();
                  console.log('[TEP Player] Play state check after 3s:', currentState);
                  if (currentState !== 'PLAYING') {
                    console.warn('[TEP Player] Player not in PLAYING state, may be stuck');
                  }
                }
              }, 3000);

              settle(resolve);
            } catch (playErr) {
              console.error('[TEP Player] play() failed after READY:', playErr);
              settle(reject, playErr);
            }
          } else {
            console.warn('[TEP Player] Unexpected state after prepareAsync:', readyState);
            settle(reject, new Error('Unexpected state: ' + readyState));
          }
        },
        () => {
          const checkState = self.getPlayerState();
          if (myRequestId !== checkState.playRequestId) {
            console.warn('[TEP Player] Aborting prepareAsync error callback — newer request in progress');
            settle(resolve);
            return;
          }
          tError('[TEP]', 'prepareAsync FAILED — bad stream or network');
          settle(reject, new Error('prepareAsync failed'));
        }
      );
    });
  }

  _resetPlayerState() {
    const playerState = this.getPlayerState();
    const state = playerState.gAVplayObj?.getState();

    if (state && state !== 'NONE' && state !== 'IDLE') {
      try {
        playerState.gAVplayObj.stop();
        playerState.gAVplayObj.close();
        console.log('[TEP Player] Player reset with stop and close.');
      } catch (e) {
        console.warn('[TEP Player] Error during reset:', e);
      }
    }
  }

  async stop() {
    const playerState = this.getPlayerState();
    if (playerState.gAVplayObj) {
      try {
        playerState.gAVplayObj.stop();
        playerState.gAVplayObj.close();
        tLog('[TEP]', 'player stopped');
      } catch (e) {
        console.warn('[TEP Player] error stopping player:', e);
      }
    }
    this._tepLayoutReady = false;
    this._tepHoleVisible = false;
    this._lastPreviewCoords = null;
    this._lastVideoDimensions = null;
    this._currentPlayUrl = null;
    this._disableTepPreviewHole();
    this._clearAvPlayerElementPosition();
  }

  /**
   * Channel zap recovery — stop/close avplay but keep TEP preview layout ready.
   */
  async resetForNextChannel() {
    const playerState = this.getPlayerState();
    if (playerState.gAVplayObj) {
      try {
        playerState.gAVplayObj.stop();
        playerState.gAVplayObj.close();
        tLog('[TEP]', 'resetForNextChannel stop+close');
      } catch (e) {
        console.warn('[TEP Player] resetForNextChannel:', e);
      }
    }
    this._currentPlayUrl = null;
    this.updatePlayerState({ isPlayerBusy: false, httpPlayTimeMs: 0, streamReady: false });
    await this.delay(50);
  }

  async setDisplayRect(coords) {
    const playerState = this.getPlayerState();
    if (playerState.gAVplayObj) {
      try {
        const x = coords.x ?? coords.left ?? 0;
        const y = coords.y ?? coords.top ?? 0;
        playerState.gAVplayObj.setDisplayRect(x, y, coords.width, coords.height);
        console.log('[TEP Player] display rect set:', { x, y, width: coords.width, height: coords.height });
      } catch (e) {
        console.error('[TEP Player] error setting display rect:', e);
      }
    }
  }

  async toggleFullscreen() {
    const playerState = this.getPlayerState();
    const newFullScreenState = !playerState.displayFullScreen;
    this.updatePlayerState({ displayFullScreen: newFullScreenState });

    const delayTime = newFullScreenState ? 50 : 150;
    await this.delay(delayTime);

    tLog('[TEP]', 'toggleFullscreen -> ' + (newFullScreenState ? 'FULLSCREEN' : 'PREVIEW'));
    this._syncPreviewDisplay(newFullScreenState);
    if (!newFullScreenState) {
      var state = playerState.gAVplayObj?.getState?.();
      if (state === 'PLAYING' || state === 'PAUSED') {
        this._revealTepPreviewVideo();
      }
    }
  }
}

export default TizenTEPPlayer;
